import {
	Button,
	Calendar,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	cn,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@monbits/ui";
import { getChain } from "@oxygen/concepts";
import { endOfDay, format, startOfDay, subDays } from "date-fns";
import { CalendarIcon, Download, Percent } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import type { TooltipProps } from "recharts";
import {
	Area,
	AreaChart,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import revenueProvider, {
	type BlockchainBreakdown,
	type CostBreakdown,
	type CurrencyBreakdown,
	type MerchantBreakdown,
	type RevenueOverview,
	type TimeSeries,
} from "src/providers/revenue-provider";

type ChartTooltipProps = TooltipProps<number, string>;

// Custom tooltip component that respects theme
const CustomTooltip = ({
	active,
	payload,
	label,
	formatter,
}: ChartTooltipProps) => {
	if (!active || !payload || payload.length === 0) return null;

	return (
		<div className="rounded-lg border bg-popover p-3 text-popover-foreground shadow-md">
			<p className="mb-2 text-xs font-medium">{label}</p>
			{payload.map((entry, index) => {
				const entryValue = entry.value ?? 0;
				const entryName = entry.name ?? "";
				let displayValue: React.ReactNode = entryValue;
				let displayName: React.ReactNode = entryName;

				if (formatter) {
					const result = formatter(
						entryValue,
						entryName,
						entry,
						index,
						payload,
					);
					if (Array.isArray(result)) {
						displayValue = result[0];
						displayName = result[1];
					} else {
						displayValue = result;
					}
				}

				return (
					<div key={index} className="flex items-center gap-2 text-xs">
						<div
							className="h-2 w-2 rounded-full"
							style={{ backgroundColor: entry.color ?? "currentColor" }}
						/>
						<span className="text-muted-foreground">{displayName}:</span>
						<span className="font-medium">{displayValue}</span>
					</div>
				);
			})}
		</div>
	);
};

const RevenuePage: React.FC = () => {
	const [overview, setOverview] = React.useState<RevenueOverview | null>(null);
	const [timeSeries, setTimeSeries] = React.useState<TimeSeries | null>(null);
	const [blockchainBreakdown, setBlockchainBreakdown] = React.useState<
		BlockchainBreakdown[]
	>([]);
	const [currencyBreakdown, setCurrencyBreakdown] = React.useState<
		CurrencyBreakdown[]
	>([]);
	const [merchantBreakdown, setMerchantBreakdown] = React.useState<
		MerchantBreakdown[]
	>([]);
	const [costBreakdown, setCostBreakdown] =
		React.useState<CostBreakdown | null>(null);

	const [isLoading, setIsLoading] = React.useState(true);
	const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);

	// Date range state - default to last 30 days
	const [dateRange, setDateRange] = React.useState<{
		from: Date;
		to: Date;
	}>({
		from: startOfDay(subDays(new Date(), 29)),
		to: endOfDay(new Date()),
	});

	const [calendarSelection, setCalendarSelection] = React.useState<
		| {
				from: Date | undefined;
				to?: Date | undefined;
		  }
		| undefined
	>(undefined);

	// Load all data when date range changes
	React.useEffect(() => {
		const loadData = async () => {
			try {
				setIsLoading(true);
				const startDate = format(dateRange.from, "yyyy-MM-dd");
				const endDate = format(dateRange.to, "yyyy-MM-dd");

				const [
					overviewData,
					timeSeriesData,
					blockchainData,
					currencyData,
					merchantData,
					costData,
				] = await Promise.all([
					revenueProvider.getOverview(startDate, endDate),
					revenueProvider.getTimeSeries(startDate, endDate),
					revenueProvider.getBlockchainBreakdown(startDate, endDate),
					revenueProvider.getCurrencyBreakdown(startDate, endDate),
					revenueProvider.getMerchantBreakdown(startDate, endDate, 10),
					revenueProvider.getCostBreakdown(startDate, endDate),
				]);

				setOverview(overviewData);
				setTimeSeries(timeSeriesData);
				setBlockchainBreakdown(blockchainData);
				setCurrencyBreakdown(currencyData);
				setMerchantBreakdown(merchantData);
				setCostBreakdown(costData);
			} catch (error) {
				console.error("Failed to load revenue data:", error);
				toast.error("Failed to load revenue data", {
					description: "Please try again later",
				});
			} finally {
				setIsLoading(false);
			}
		};
		loadData();
	}, [dateRange]);

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(amount);
	};

	const formatPercent = (value: number) => {
		return `${(value * 100).toFixed(1)}%`;
	};

	const [isExporting, setIsExporting] = React.useState(false);

	const handleExport = async () => {
		try {
			setIsExporting(true);
			const startDate = format(dateRange.from, "yyyy-MM-dd");
			const endDate = format(dateRange.to, "yyyy-MM-dd");

			const { data, filename } = await revenueProvider.exportCSV(
				startDate,
				endDate,
			);

			// Create download link
			const url = URL.createObjectURL(data);
			const link = document.createElement("a");
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);

			toast.success("Export downloaded successfully");
		} catch (error) {
			console.error("Failed to export revenue data:", error);
			toast.error("Failed to export revenue data");
		} finally {
			setIsExporting(false);
		}
	};

	const handleCalendarSelect = (
		range: { from: Date | undefined; to?: Date | undefined } | undefined,
	) => {
		setCalendarSelection(range);
	};

	const handleApplyDateRange = () => {
		if (calendarSelection?.from && calendarSelection?.to) {
			setDateRange({
				from: startOfDay(calendarSelection.from),
				to: endOfDay(calendarSelection.to),
			});
			setIsCalendarOpen(false);
			setCalendarSelection(undefined);
		}
	};

	const handleCancelDateRange = () => {
		setIsCalendarOpen(false);
		setCalendarSelection(undefined);
	};

	const handleCalendarOpenChange = (open: boolean) => {
		setIsCalendarOpen(open);
		if (!open) {
			setCalendarSelection(undefined);
		}
	};

	const hasValidRange =
		calendarSelection?.from &&
		calendarSelection?.to &&
		calendarSelection.from.toDateString() !==
			calendarSelection.to.toDateString();

	// Prepare chart data
	const chartData =
		timeSeries?.data.map((point) => ({
			date: point.date,
			revenue: point.orderRevenueUsd + point.withdrawalRevenueUsd,
			cost: point.amlCostUsd + point.energyCostUsd,
		})) || [];

	if (isLoading) {
		return (
			<div className="space-y-6 p-4 sm:p-6 lg:p-8">
				<Skeleton className="h-12 w-64" />
				<Skeleton className="h-56" />
				<Skeleton className="h-80" />
				<Skeleton className="h-64" />
			</div>
		);
	}

	const handlePresetSelect = (days: number) => {
		setDateRange({
			from: startOfDay(subDays(new Date(), days - 1)),
			to: endOfDay(new Date()),
		});
		setIsCalendarOpen(false);
	};

	return (
		<div className="space-y-6 p-4 sm:p-6 lg:p-8">
			{/* Header */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
				<div>
					<h1 className="text-3xl font-bold">Revenue & Profit</h1>
					<p className="text-sm text-muted-foreground mt-1">
						Track revenue, costs, and profit across your platform
					</p>
				</div>
				<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
					<Popover
						open={isCalendarOpen}
						onOpenChange={handleCalendarOpenChange}
					>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									"w-full sm:w-[260px] justify-start text-left font-normal",
									!dateRange && "text-muted-foreground",
								)}
							>
								<CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
								{dateRange.from && dateRange.to ? (
									<span className="truncate">
										{format(dateRange.from, "MMM d, yyyy")} -{" "}
										{format(dateRange.to, "MMM d, yyyy")}
									</span>
								) : (
									<span>Pick a date range</span>
								)}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="end">
							<div className="flex flex-col">
								{/* Quick presets */}
								<div className="flex flex-wrap gap-1 p-3 border-b">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handlePresetSelect(7)}
									>
										7 days
									</Button>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handlePresetSelect(30)}
									>
										30 days
									</Button>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handlePresetSelect(90)}
									>
										90 days
									</Button>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handlePresetSelect(365)}
									>
										1 year
									</Button>
								</div>
								<Calendar
									mode="range"
									defaultMonth={dateRange.from}
									selected={calendarSelection}
									onSelect={handleCalendarSelect}
									numberOfMonths={1}
									disabled={(date) =>
										date > new Date() || date < new Date("2020-01-01")
									}
									className="sm:hidden"
								/>
								<Calendar
									mode="range"
									defaultMonth={dateRange.from}
									selected={calendarSelection}
									onSelect={handleCalendarSelect}
									numberOfMonths={2}
									disabled={(date) =>
										date > new Date() || date < new Date("2020-01-01")
									}
									className="hidden sm:block"
								/>
								<div className="flex items-center justify-end gap-2 p-3 border-t">
									<Button
										variant="outline"
										size="sm"
										onClick={handleCancelDateRange}
									>
										Cancel
									</Button>
									<Button
										size="sm"
										onClick={handleApplyDateRange}
										disabled={!hasValidRange}
									>
										Apply
									</Button>
								</div>
							</div>
						</PopoverContent>
					</Popover>
					<Button
						variant="outline"
						onClick={handleExport}
						disabled={isExporting}
						className="w-full sm:w-auto"
					>
						<Download className="mr-2 h-4 w-4" />
						{isExporting ? "Exporting..." : "Export CSV"}
					</Button>
				</div>
			</div>

			<div className="stagger-in rounded-xl border bg-card p-6 sm:p-8">
				<div>
					<p className="text-sm font-medium text-muted-foreground">
						Net Profit
					</p>
					<p className="text-4xl sm:text-5xl font-bold tracking-tight tabular-nums mt-3">
						{formatCurrency(overview?.netProfitUsd || 0)}
					</p>
					<div className="flex items-center gap-4 mt-4 flex-wrap">
						<span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--trend-positive)]/15 px-2.5 py-1 text-xs font-semibold text-[var(--trend-positive)]">
							<Percent className="size-3" />
							{formatPercent(overview?.profitMargin || 0)} margin
						</span>
						<span className="text-xs text-muted-foreground">
							{format(dateRange.from, "MMM d")} —{" "}
							{format(dateRange.to, "MMM d, yyyy")}
						</span>
					</div>
				</div>

				<div className="mt-6 pt-6 border-t grid grid-cols-1 sm:grid-cols-2 gap-6">
					<div>
						<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
							Revenue
						</p>
						<p className="text-2xl font-semibold tracking-tight tabular-nums mt-1.5">
							{formatCurrency(overview?.totalRevenueUsd || 0)}
						</p>
						<p className="text-xs text-muted-foreground mt-1">
							Orders {formatCurrency(overview?.orderRevenueUsd || 0)}
						</p>
					</div>
					<div>
						<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
							Costs
						</p>
						<p className="text-2xl font-semibold tracking-tight tabular-nums mt-1.5">
							{formatCurrency(overview?.totalCostUsd || 0)}
						</p>
						<p className="text-xs text-muted-foreground mt-1">
							AML {formatCurrency(overview?.amlCostUsd || 0)} &middot; Energy{" "}
							{formatCurrency(overview?.energyCostUsd || 0)}
						</p>
					</div>
				</div>

				<p className="text-xs text-muted-foreground mt-6 pt-6 border-t">
					Net profit is a P&amp;L figure. Energy and AML costs are paid from
					external accounts, not from wallet funds, so profit is not the same as
					cash on hand. For funds you can actually withdraw, see{" "}
					<Link to="/treasury" className="underline underline-offset-2">
						Treasury
					</Link>
					.
				</p>
			</div>

			<Card>
				<CardHeader className="pb-2">
					<CardTitle className="text-base font-semibold">
						Revenue & Costs
					</CardTitle>
					<CardDescription className="text-xs">
						Daily totals for the selected period
					</CardDescription>
				</CardHeader>
				<CardContent className="pl-0 sm:pl-6">
					<ResponsiveContainer
						width="100%"
						height={220}
						className="sm:!h-[280px]"
					>
						<AreaChart data={chartData}>
							<defs>
								<linearGradient
									id="revenueGradient"
									x1="0"
									y1="0"
									x2="0"
									y2="1"
								>
									<stop
										offset="0%"
										stopColor="var(--chart-1)"
										stopOpacity={0.5}
									/>
									<stop
										offset="50%"
										stopColor="var(--chart-1)"
										stopOpacity={0.25}
									/>
									<stop
										offset="100%"
										stopColor="var(--chart-1)"
										stopOpacity={0}
									/>
								</linearGradient>
								<linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
									<stop
										offset="0%"
										stopColor="var(--muted-foreground)"
										stopOpacity={0.5}
									/>
									<stop
										offset="50%"
										stopColor="var(--muted-foreground)"
										stopOpacity={0.25}
									/>
									<stop
										offset="100%"
										stopColor="var(--muted-foreground)"
										stopOpacity={0}
									/>
								</linearGradient>
							</defs>
							<XAxis
								dataKey="date"
								tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
								tickLine={false}
								axisLine={false}
								tickFormatter={(value) => {
									const date = new Date(value);
									return `${date.getMonth() + 1}/${date.getDate()}`;
								}}
							/>
							<YAxis
								tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
								tickLine={false}
								axisLine={false}
								tickFormatter={(value) =>
									value >= 1000
										? `$${(value / 1000).toFixed(0)}k`
										: `$${value.toFixed(0)}`
								}
							/>
							<Tooltip
								content={
									<CustomTooltip
										formatter={(value: number, name: string) => [
											formatCurrency(value),
											name.charAt(0).toUpperCase() + name.slice(1),
										]}
									/>
								}
							/>
							<Legend
								wrapperStyle={{ paddingTop: "20px" }}
								iconType="circle"
								formatter={(value: string) => (
									<span className="text-xs font-medium text-muted-foreground">
										{value.charAt(0).toUpperCase() + value.slice(1)}
									</span>
								)}
							/>
							<Area
								type="monotone"
								dataKey="revenue"
								stroke="var(--chart-1)"
								strokeWidth={2.5}
								fill="url(#revenueGradient)"
								dot={false}
								name="Revenue"
							/>
							<Area
								type="monotone"
								dataKey="cost"
								stroke="var(--muted-foreground)"
								strokeWidth={2.5}
								fill="url(#costGradient)"
								dot={false}
								name="Cost"
							/>
						</AreaChart>
					</ResponsiveContainer>
				</CardContent>
			</Card>

			{/* Breakdowns */}
			<Tabs defaultValue="blockchain" className="space-y-6">
				<TabsList>
					<TabsTrigger value="blockchain">By Blockchain</TabsTrigger>
					<TabsTrigger value="currency">By Currency</TabsTrigger>
					<TabsTrigger value="merchant">By Merchant</TabsTrigger>
				</TabsList>

				<TabsContent value="blockchain">
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm sm:text-base font-medium">
								Revenue by Blockchain
							</CardTitle>
							<CardDescription className="text-xs">
								Breakdown of revenue and costs by blockchain network
							</CardDescription>
						</CardHeader>
						<CardContent>
							{blockchainBreakdown.length === 0 ? (
								<div className="flex flex-col items-center justify-center py-12">
									<p className="text-sm text-muted-foreground">
										No data available for the selected period
									</p>
								</div>
							) : (
								<div className="overflow-x-auto -mx-6 px-6">
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Blockchain</TableHead>
												<TableHead className="text-right">
													Order Revenue
												</TableHead>
												<TableHead className="text-right">
													Withdrawal Revenue
												</TableHead>
												<TableHead className="text-right">
													Energy Cost
												</TableHead>
												<TableHead className="text-right">
													Net Revenue
												</TableHead>
												<TableHead className="text-right">Volume</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{blockchainBreakdown.map((row) => (
												<TableRow key={row.blockchain}>
													<TableCell className="font-medium">
														<div className="flex items-center gap-2">
															<CryptoIcon
																ticker={row.blockchain}
																className="h-5 w-5"
															/>
															{getChain(row.blockchain)?.label ??
																row.blockchain}
														</div>
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{formatCurrency(row.orderRevenueUsd)}
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{formatCurrency(row.withdrawalRevenueUsd)}
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{formatCurrency(row.energyCostUsd)}
													</TableCell>
													<TableCell className="text-right font-medium tabular-nums">
														{formatCurrency(row.netRevenueUsd)}
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{formatCurrency(row.volumeUsd)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							)}
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="currency">
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm sm:text-base font-medium">
								Revenue by Currency
							</CardTitle>
							<CardDescription className="text-xs">
								Breakdown of revenue by cryptocurrency
							</CardDescription>
						</CardHeader>
						<CardContent>
							{currencyBreakdown.length === 0 ? (
								<div className="flex flex-col items-center justify-center py-12">
									<p className="text-sm text-muted-foreground">
										No data available for the selected period
									</p>
								</div>
							) : (
								<div className="overflow-x-auto -mx-6 px-6">
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Currency</TableHead>
												<TableHead className="text-right">
													Revenue (USD)
												</TableHead>
												<TableHead className="text-right">
													Order Count
												</TableHead>
												<TableHead className="text-right">
													Volume (USD)
												</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{currencyBreakdown.map((row) => (
												<TableRow key={row.currency}>
													<TableCell className="font-medium">
														<div className="flex items-center gap-2">
															<CryptoIcon
																ticker={row.currency}
																showComposite
																className="h-5 w-5"
															/>
															{row.currency}
														</div>
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{formatCurrency(row.revenueUsd)}
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{row.orderCount}
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{formatCurrency(row.volumeUsd)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							)}
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="merchant">
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-sm sm:text-base font-medium">
								Revenue by Merchant
							</CardTitle>
							<CardDescription className="text-xs">
								Top merchants by revenue (top 10)
							</CardDescription>
						</CardHeader>
						<CardContent>
							{merchantBreakdown.length === 0 ? (
								<div className="flex flex-col items-center justify-center py-12">
									<p className="text-sm text-muted-foreground">
										No data available for the selected period
									</p>
								</div>
							) : (
								<div className="overflow-x-auto -mx-6 px-6">
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Merchant</TableHead>
												<TableHead className="hidden sm:table-cell">
													User Email
												</TableHead>
												<TableHead className="text-right">
													Order Revenue
												</TableHead>
												<TableHead className="text-right hidden sm:table-cell">
													Withdrawal Revenue
												</TableHead>
												<TableHead className="text-right">
													Total Revenue
												</TableHead>
												<TableHead className="text-right hidden lg:table-cell">
													Volume
												</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{merchantBreakdown.map((row) => (
												<TableRow key={row.merchantId}>
													<TableCell className="font-medium">
														{row.merchantName}
													</TableCell>
													<TableCell className="text-muted-foreground hidden sm:table-cell">
														{row.userEmail}
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{formatCurrency(row.orderRevenueUsd)}
													</TableCell>
													<TableCell className="text-right hidden sm:table-cell tabular-nums">
														{formatCurrency(row.withdrawalRevenueUsd)}
													</TableCell>
													<TableCell className="text-right font-medium tabular-nums">
														{formatCurrency(row.totalRevenueUsd)}
													</TableCell>
													<TableCell className="text-right hidden lg:table-cell tabular-nums">
														{formatCurrency(row.volumeUsd)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							)}
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>

			{/* Cost Breakdown */}
			<Card>
				<CardHeader className="pb-2">
					<CardTitle className="text-sm sm:text-base font-medium">
						Cost Breakdown
					</CardTitle>
					<CardDescription className="text-xs">
						Detailed breakdown of all operational costs
					</CardDescription>
				</CardHeader>
				<CardContent>
					{costBreakdown ? (
						<div className="overflow-x-auto -mx-6 px-6">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Cost Type</TableHead>
										<TableHead className="text-right">Count</TableHead>
										<TableHead className="text-right">Cost (USD)</TableHead>
										<TableHead className="text-right hidden sm:table-cell">
											Cost (TRX)
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									<TableRow>
										<TableCell className="font-medium">AML Checks</TableCell>
										<TableCell className="text-right tabular-nums">
											{costBreakdown.amlCheckCount}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{formatCurrency(costBreakdown.amlCostUsd)}
										</TableCell>
										<TableCell className="text-right text-muted-foreground hidden sm:table-cell">
											-
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell className="font-medium">
											<span className="hidden sm:inline">
												Energy (Internal Transfers)
											</span>
											<span className="sm:hidden">Internal Transfers</span>
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{costBreakdown.energyInternalCount}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{formatCurrency(costBreakdown.energyInternalCostUsd)}
										</TableCell>
										<TableCell className="text-right hidden sm:table-cell tabular-nums">
											{costBreakdown.energyInternalCostTrx.toFixed(2)} TRX
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell className="font-medium">
											<span className="hidden sm:inline">
												Energy (Merchant Withdrawals)
											</span>
											<span className="sm:hidden">Merchant Withdrawals</span>
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{costBreakdown.energyMerchantWithdrawalCount}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{formatCurrency(
												costBreakdown.energyMerchantWithdrawalCostUsd,
											)}
										</TableCell>
										<TableCell className="text-right hidden sm:table-cell tabular-nums">
											{costBreakdown.energyMerchantWithdrawalCostTrx.toFixed(2)}{" "}
											TRX
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell className="font-medium">
											<span className="hidden sm:inline">
												Energy (Pool Withdrawals)
											</span>
											<span className="sm:hidden">Pool Withdrawals</span>
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{costBreakdown.energyPoolWithdrawalCount}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{formatCurrency(
												costBreakdown.energyPoolWithdrawalCostUsd,
											)}
										</TableCell>
										<TableCell className="text-right hidden sm:table-cell tabular-nums">
											{costBreakdown.energyPoolWithdrawalCostTrx.toFixed(2)} TRX
										</TableCell>
									</TableRow>
									<TableRow className="border-t-2 font-bold">
										<TableCell>Total</TableCell>
										<TableCell className="text-right tabular-nums">-</TableCell>
										<TableCell className="text-right tabular-nums">
											{formatCurrency(costBreakdown.totalCostUsd)}
										</TableCell>
										<TableCell className="text-right hidden sm:table-cell tabular-nums">
											{costBreakdown.totalEnergyCostTrx.toFixed(2)} TRX
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</div>
					) : (
						<div className="flex flex-col items-center justify-center py-12">
							<p className="text-sm text-muted-foreground">
								No cost data available
							</p>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
};

export default RevenuePage;
